import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, NgModule } from '@angular/core';
import { AppComponent } from './app.component';
import { AppRouting } from '@app/app.routing';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ModulePreLoading } from '@app/module-pre-loading';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { ToastrModule } from 'ngx-toastr';
import {
    DateAdapter,
    ErrorStateMatcher,
    MAT_DATE_FORMATS,
    ShowOnDirtyErrorStateMatcher,
} from '@angular/material/core';
import { APP_DATE_FORMATS, AppLuxonDateAdapter } from '@core/adapters/datepicker.adapter';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { SharedComponentModule } from '@modules/shared/shared-component.module';
import { HttpErrorInterceptor } from '@core/interceptors/http-error.interceptor';
import { OverlayModule } from '@angular/cdk/overlay';
import { Angulartics2Module } from 'angulartics2';
import { ErrorService } from '@core/services/error.service';
import { AppConstant } from '@app/app.constant';
import { RedocConnectModule } from '@app/shared/redoc-connect/redoc-connect.module';
import { AuthService } from '@modules/auth/auth.service';
import { SSOAuthService } from '@modules/auth/sso-auth.service';
import { environment } from '../environments/environment';
import { RedrConfigModule } from './modules/layout/redr-config.module';
import { DBConfig, NgxIndexedDBModule } from 'ngx-indexed-db';
import { HttpLogInterceptor } from '@core/interceptors/http-log.interceptor';
import { AuthInterceptor } from '@core/interceptors/auth.interceptor';
import { GrecaptchaModule } from '@shared/grecaptcha';
import { RecaptchaInterceptor } from './core/interceptors/recaptcha.interceptor';

// 'legacy' | 'standard' | 'fill' | 'outline'
const formFieldDefault: MatFormFieldDefaultOptions = {
    appearance: 'outline',
    floatLabel: 'always'
};

const materialProviders = [
    {
        provide: MAT_DATE_FORMATS,
        useValue: APP_DATE_FORMATS
    },
    {
        provide: DateAdapter,
        useClass: AppLuxonDateAdapter
    },
    {
        provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
        useValue: formFieldDefault
    },
    {
        provide: ErrorStateMatcher,
        useClass: ShowOnDirtyErrorStateMatcher
    }
];



const dbConfig: DBConfig = {
    name: 'RedocSuperAdminDB',
    version: 1,
    objectStoresMeta: [{
        store: 'templates',
        storeConfig: { keyPath: 'id', autoIncrement: true },
        storeSchema: [
            { name: 'userId', keypath: 'userId', options: { unique: false } },
            { name: 'templateId', keypath: 'templateId', options: { unique: true } },
            { name: 'content', keypath: 'content', options: { unique: false } },
            { name: 'key', keypath: 'key', options: { unique: false } },
            { name: 'createdAt', keypath: 'createdAt', options: { unique: false } },
            { name: 'updatedAt', keypath: 'updatedAt', options: { unique: false } },
        ]
    }]
};

@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        RedocConnectModule.forRoot(environment.redocConnect),
        FormsModule,
        ReactiveFormsModule,
        SharedComponentModule,
        AppRouting,
        GrecaptchaModule.forRoot({ siteKey: AppConstant.APP_RECAPTCHA_SITEKEY }),
        Angulartics2Module.forRoot({
            developerMode: AppConstant.APP_DEVELOPER_MODE,
            gst: {
                trackingIds: ['UA-173398124-1'],
                anonymizeIp: true,
            }
        }),
        ToastrModule.forRoot({
        // toastComponent: CustomToastComponent
        }),
        OverlayModule,
        RedrConfigModule.forRoot(),
        NgxIndexedDBModule.forRoot(dbConfig)], providers: [
        ModulePreLoading,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: RecaptchaInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpLogInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        {
            // SSO is the only implementation of the session contract that ever runs.
            provide: AuthService,
            useClass: SSOAuthService,
        },
        {
            provide: ErrorHandler,
            useClass: ErrorService
        },
        ...materialProviders,
        provideHttpClient(withInterceptorsFromDi()),
        provideTranslateService({
            fallbackLang: 'en',
            loader: provideTranslateHttpLoader({
                prefix: './assets/i18n/app/',
                suffix: '.json',
            }),
        })
    ] })
export class AppModule {
}
